import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ChevronRight, Home } from 'lucide-react';

/**
 * BreadcrumbNav - SEO-Optimized Breadcrumb Navigation
 * 
 * Features:
 * - Auto-generates BreadcrumbList schema for Google
 * - Responsive design (collapses on mobile)
 * - Accessible navigation structure
 * - Supports custom breadcrumb paths
 * 
 * Critical for 2026 AIO: Helps Google understand site structure
 */

const SITE_URL = 'https://xtreamgate.com';

// Route name mappings for auto-generated breadcrumbs
const ROUTE_NAMES = {
  '': 'Home',
  'blog': 'Blog & Guides',
  'about': 'About Us',
  'security': 'Security',
  'support': 'Support',
  'report': 'Report',
  'iptv-firestick': 'IPTV for Firestick',
  'iptv-samsung-tv': 'IPTV for Samsung TV',
  'iptv-lg-tv': 'IPTV for LG TV',
  'iptv-apple-tv': 'IPTV for Apple TV',
  'iptv-android': 'IPTV for Android',
  'iptv-roku': 'IPTV for Roku',
  'iptv-windows-pc': 'IPTV for Windows',
  'iptv-mac': 'IPTV for Mac',
  'iptv-usa': 'IPTV USA',
  'iptv-canada': 'IPTV Canada',
  'iptv-uk': 'IPTV UK',
  'iptv-australia': 'IPTV Australia'
};

/**
 * Generate BreadcrumbList Schema
 */
const generateBreadcrumbSchema = (breadcrumbs) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': crumb.name,
      'item': `${SITE_URL}${crumb.path}`
    }))
  };
};

/**
 * Auto-generate breadcrumbs from current path
 */
const generateBreadcrumbs = (pathname, customTitle = null) => {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbs = [{ name: 'Home', path: '/' }];

  let currentPath = '';
  paths.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Handle blog article slugs
    if (paths[index - 1] === 'blog' && segment !== 'blog') {
      // This is a blog article slug - use custom title if provided
      breadcrumbs.push({
        name: customTitle || formatSlug(segment),
        path: currentPath
      });
    } else {
      breadcrumbs.push({
        name: ROUTE_NAMES[segment] || formatSlug(segment),
        path: currentPath
      });
    }
  });

  return breadcrumbs;
};

/**
 * Format slug to readable title
 */
const formatSlug = (slug) => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const BreadcrumbNav = ({ 
  items = null, // Custom breadcrumb items override
  currentPageTitle = null, // Title for current page (especially for articles)
  showSchema = true,
  variant = 'default' // 'default', 'minimal', 'rich'
}) => {
  const location = useLocation();
  
  // Use custom items or auto-generate
  const breadcrumbs = items || generateBreadcrumbs(location.pathname, currentPageTitle);

  if (breadcrumbs.length <= 1) return null;

  const variantStyles = {
    default: 'bg-card/30 border border-white/10 rounded-lg px-4 py-3',
    minimal: 'py-2',
    rich: 'bg-gradient-to-r from-card/50 to-transparent border border-white/10 rounded-lg px-4 py-3'
  };

  return (
    <>
      {/* Schema Markup */}
      {showSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(generateBreadcrumbSchema(breadcrumbs))}
          </script>
        </Helmet>
      )}

      {/* Visual Breadcrumbs */}
      <nav 
        aria-label="Breadcrumb" 
        className={`${variantStyles[variant]} mb-6`}
      >
        <ol 
          className="flex flex-wrap items-center gap-1 text-sm"
          itemScope 
          itemType="https://schema.org/BreadcrumbList"
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            const isFirst = index === 0;

            return (
              <li 
                key={crumb.path}
                className="flex items-center"
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                {/* Separator */}
                {!isFirst && (
                  <ChevronRight className="w-4 h-4 mx-1 text-muted-foreground" />
                )}

                {/* Link or Current Page */}
                {isLast ? (
                  <span 
                    className="text-foreground font-medium truncate max-w-[200px] md:max-w-none"
                    itemProp="name"
                    aria-current="page"
                  >
                    {crumb.name}
                  </span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="flex items-center gap-1 text-muted-foreground hover:text-primary transition"
                    itemProp="item"
                  >
                    {isFirst && <Home className="w-4 h-4" />}
                    <span itemProp="name">{crumb.name}</span>
                  </Link>
                )}

                {/* Schema position */}
                <meta itemProp="position" content={String(index + 1)} />
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

/**
 * BreadcrumbNavCompact - Inline minimal version
 */
export const BreadcrumbNavCompact = ({ items = null, currentPageTitle = null }) => {
  const location = useLocation();
  const breadcrumbs = items || generateBreadcrumbs(location.pathname, currentPageTitle);

  if (breadcrumbs.length <= 1) return null;

  return (
    <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-4">
      <ol className="flex flex-wrap items-center gap-1">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <li key={crumb.path} className="flex items-center">
              {index > 0 && <span className="mx-1">/</span>}
              {isLast ? (
                <span className="text-foreground">{crumb.name}</span>
              ) : (
                <Link to={crumb.path} className="hover:text-primary transition">
                  {crumb.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

/**
 * Helper: Create breadcrumb items for blog articles
 */
export const createBlogBreadcrumbs = (article) => {
  return [
    { name: 'Home', path: '/' },
    { name: 'Blog & Guides', path: '/blog' },
    { name: article.category, path: `/blog?category=${encodeURIComponent(article.category)}` },
    { name: article.title, path: `/blog/${article.slug}` }
  ];
};

/**
 * Helper: Create breadcrumb items for device pages
 */
export const createDeviceBreadcrumbs = (device) => {
  return [
    { name: 'Home', path: '/' },
    { name: 'IPTV Setup Guides', path: '/blog?category=Setup%20Guides' },
    { name: `IPTV for ${device}`, path: `/iptv-${device.toLowerCase().replace(' ', '-')}` }
  ];
};

/**
 * Helper: Create breadcrumb items for regional pages
 */
export const createRegionalBreadcrumbs = (region) => {
  return [
    { name: 'Home', path: '/' },
    { name: 'Regional IPTV', path: '/blog?category=Regional%20IPTV' },
    { name: `IPTV ${region}`, path: `/iptv-${region.toLowerCase()}` }
  ];
};

export default BreadcrumbNav;
